import { createSelector } from '@reduxjs/toolkit';
import { find, get, getOr, includes, map, pipe, some, sortBy } from 'lodash/fp';
import { Defaults } from 'app/lib/constants';
import { PermittedOperations, PermittedCapabilities } from './constants';

const selectUsersSlice = get('users');

export const selectCurrentUserName = createSelector(selectUsersSlice, get('currentUserId'));
export const selectMaximumNumberOfAccounts = createSelector(
    selectUsersSlice,
    get('maximumNumberOfAccounts')
);
export const selectRestrictOnMaximumNumberOfAccounts = createSelector(
    selectUsersSlice,
    get('restrictOnMaximumNumberOfAccounts')
);
export const selectUsersByUserId = createSelector(selectUsersSlice, get('byUserId'));
export const selectIsLoadingUsers = createSelector(selectUsersSlice, get('isLoading'));

export const selectCurrentUser = createSelector(
    selectUsersByUserId,
    selectCurrentUserName,
    (byUserId, currentUserName) => byUserId[currentUserName]
);

export const selectUsers = createSelector([selectUsersByUserId], (usersById) =>
    sortBy('username', usersById)
);

const selectGroups = createSelector(selectUsersSlice, get('groups'));

export const selectGroupNames = createSelector(selectGroups, map('name'));

export const selectDefaultGroupName = createSelector(
    selectGroups,
    pipe(find('isDefault'), get('name'))
);

export const selectUserHasAcceptedTerms = get('userAuth.termsAccepted');

export const selectIsUserFullyAuthenticated = createSelector(
    [get('userAuth.keycloakReady'), get('userAuth.wsReady')],
    (kcReady, wsReady) => kcReady && wsReady
);

const selectPermittedOperations = createSelector(
    selectCurrentUser,
    getOr(Defaults.ARRAY, 'permittedOperations')
);

export const selectUserCanManageUsers = createSelector(
    selectPermittedOperations,
    (operations) =>
        includes(PermittedOperations.LIST_USER_DETAILS, operations) &&
        includes(PermittedOperations.CREATE_USER, operations)
);

const selectPermittedCapabilities = createSelector(
    selectCurrentUser,
    getOr(Defaults.ARRAY, 'capabilities')
);

export const selectUserCanModifyEverything = createSelector(
    [selectPermittedCapabilities],
    (capabilities) => capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageGlossary = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_GLOSSARY) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageJobs = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_SYSTEMS) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageSnowflake = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_SYSTEMS) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageProperties = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_PROPERTIES) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageIntegrations = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_SYSTEMS) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectUserCanManageAdvancedConfiguration = createSelector(
    [selectPermittedCapabilities],
    (capabilities) =>
        capabilities.includes(PermittedCapabilities.MANAGE_SYSTEMS) ||
        capabilities.includes(PermittedCapabilities.MODIFY_EVERYTHING)
);

export const selectIsAdmin = createSelector(
    [
        selectUserCanManageUsers,
        selectUserCanManageProperties,
        selectUserCanManageGlossary,
        selectUserCanManageSnowflake
    ],
    (...args) => some(Boolean, args)
);
